import { gsap } from 'gsap';

/**
 * Accordion animator
 * Smooth, resilient open/close animations for details/summary accordions
 */
export default class AccordionAnimator {
    constructor() {
        this.ACCORDION_SELECTOR = '.accordion-container .accordion-item';
        this.WP_ACCORDION_SELECTOR = '.wp-block-accordion-item';
        this.SUMMARY_SELECTOR = '.accordion-summary';
        this.WP_TOGGLE_SELECTOR = '.wp-block-accordion-heading__toggle';
        this.WP_PANEL_SELECTOR = '.wp-block-accordion-panel';
        this.CONTENT_SELECTOR = '.accordion-content';
        this.ANIMATION = {
            DURATION: 0.9,
            EASE: 'power3.inOut'
        };

        this.paddingMap = new WeakMap();

        // Initialize module accordions
        const moduleItems = Array.from(document.querySelectorAll(this.ACCORDION_SELECTOR));
        moduleItems.forEach((details) => this.initializeItem(details, true));

        // Initialize WordPress accordion blocks
        // Filter to only include blocks that are NOT inside accordion modules
        const allWpBlocks = Array.from(document.querySelectorAll(this.WP_ACCORDION_SELECTOR));
        const wpItems = allWpBlocks.filter(block => !block.closest('.accordion-container'));
        
        if (wpItems.length > 0) {
            this.setupWordPressAccordions(wpItems);
            wpItems.forEach((item) => this.initializeWordPressAccordionItem(item));
        }
    }

    setupWordPressAccordions(items) {
        // Group items by their parent .wp-block-accordion container
        const accordionContainers = new Map();
        items.forEach((item) => {
            const container = item.closest('.wp-block-accordion');
            if (container) {
                if (!accordionContainers.has(container)) {
                    accordionContainers.set(container, []);
                }
                accordionContainers.get(container).push(item);
            }
        });

        // Process each accordion container
        const allContainers = Array.from(accordionContainers.keys());
        allContainers.forEach((container, containerIndex) => {
            const containerItems = accordionContainers.get(container);
            
            containerItems.forEach((item) => {
                const toggle = item.querySelector(this.WP_TOGGLE_SELECTOR);
                if (!toggle) return;

                // Add js-reveal class for reveal animations
                if (!item.classList.contains('js-reveal')) {
                    item.classList.add('js-reveal');
                }

                // Hide the default "+" icon
                const defaultIcon = toggle.querySelector('.wp-block-accordion-heading__toggle-icon');
                if (defaultIcon) {
                    defaultIcon.style.display = 'none';
                }

                // Check if our custom icon already exists
                if (!toggle.querySelector('.accordion-icon')) {
                    const title = toggle.querySelector('.wp-block-accordion-heading__toggle-title');
                    
                    // Wrap toggle title if needed (for proper flex layout)
                    if (title && !toggle.querySelector('.flex')) {
                        const wrapper = document.createElement('div');
                        wrapper.className = 'flex items-center justify-between';
                        wrapper.style.width = '100%';
                        
                        // Insert wrapper before title, then move title into it
                        toggle.insertBefore(wrapper, title);
                        wrapper.appendChild(title);
                    }
                    
                    // Create icon container with inline SVG
                    const iconSpan = document.createElement('span');
                    iconSpan.className = 'accordion-icon';
                    iconSpan.setAttribute('aria-hidden', 'true');
                    iconSpan.innerHTML = '<svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.75 7.5L9.5 12.5L3.25 7.5" stroke="currentColor" stroke-width="1.5"/></svg>';
                    
                    // Add icon to toggle (it will be positioned absolutely relative to toggle)
                    toggle.appendChild(iconSpan);
                }
            });

            // Add gradient divider after each accordion container (except the last)
            if (containerIndex < allContainers.length - 1) {
                const nextContainer = container.nextElementSibling;
                // Check if divider doesn't already exist
                if (!nextContainer || !nextContainer.classList.contains('wp-block-accordion-divider')) {
                    const divider = document.createElement('div');
                    divider.className = 'wp-block-accordion-divider js-reveal';
                    container.parentNode.insertBefore(divider, container.nextSibling);
                }
            }
        });
    }

    initializeWordPressAccordionItem(item) {
        const toggle = item.querySelector(this.WP_TOGGLE_SELECTOR);
        const panel = item.querySelector(this.WP_PANEL_SELECTOR);
        
        if (!toggle || !panel) return;

        // Cache initial paddings to animate without jumps
        const computed = getComputedStyle(panel);
        const originalPaddingTop = parseFloat(computed.paddingTop) || 0;
        const originalPaddingBottom = parseFloat(computed.paddingBottom) || 0;
        this.paddingMap.set(panel, { pt: originalPaddingTop, pb: originalPaddingBottom });

        // Check initial state (WordPress uses inert attribute and is-open class)
        const isOpen = item.classList.contains('is-open') || !panel.hasAttribute('inert');
        
        // Ensure initial state: closed panels have 0 height and no padding; open panels use natural paddings
        if (isOpen) {
            panel.style.height = 'auto';
            panel.style.overflow = 'hidden';
            panel.style.paddingTop = `${originalPaddingTop}px`;
            panel.style.paddingBottom = `${originalPaddingBottom}px`;
            panel.style.minHeight = 'auto';
            panel.style.maxHeight = 'none';
        } else {
            panel.style.height = '0px';
            panel.style.overflow = 'hidden';
            panel.style.paddingTop = '0px';
            panel.style.paddingBottom = '0px';
            panel.style.minHeight = '0px';
            panel.style.maxHeight = '0px';
        }

        // Track previous state to detect changes
        let previousState = item.classList.contains('is-open') || !panel.hasAttribute('inert');
        
        // Use MutationObserver to watch for state changes
        const observer = new MutationObserver(() => {
            const currentState = item.classList.contains('is-open') || !panel.hasAttribute('inert');
            
            if (currentState !== previousState) {
                // Kill any ongoing tweens
                gsap.killTweensOf(panel);
                
                if (currentState) {
                    // WordPress opened it, animate it open
                    this.animateWordPressOpen(item, panel);
                    
                    // Close other open items in the same parent container
                    const parent = item.parentElement;
                    if (parent) {
                        const siblings = Array.from(parent.querySelectorAll('.wp-block-accordion-item.is-open')).filter((el) => el !== item);
                        siblings.forEach((openItem) => {
                            const openPanel = openItem.querySelector(this.WP_PANEL_SELECTOR);
                            if (openPanel) {
                                gsap.killTweensOf(openPanel);
                                this.animateWordPressClose(openItem, openPanel);
                            }
                        });
                    }
                } else {
                    // WordPress closed it, animate it closed
                    this.animateWordPressClose(item, panel);
                }
                
                previousState = currentState;
            }
        });
        
        // Observe changes to classList and attributes
        observer.observe(item, { attributes: true, attributeFilter: ['class'] });
        observer.observe(panel, { attributes: true, attributeFilter: ['inert'] });

        // Keyboard accessibility
        toggle.addEventListener('keydown', (event) => {
            const key = event.key;
            if (key === 'Enter' || key === ' ') {
                event.preventDefault();
                toggle.click();
            }
        });
    }

    initializeItem(details, isModuleAccordion = true) {
        // For module accordions only
        const summary = details.querySelector(this.SUMMARY_SELECTOR);
        const content = details.querySelector(this.CONTENT_SELECTOR);
        
        if (!summary || !content) return;

        // Disable native <details> grouping behavior to avoid abrupt close
        // Preserve grouping intent via dataset
        if (details.hasAttribute('name')) {
            details.dataset.group = details.getAttribute('name') || '';
            details.removeAttribute('name');
        }

        // Cache initial paddings to animate without jumps
        const computed = getComputedStyle(content);
        const originalPaddingTop = parseFloat(computed.paddingTop) || 0;
        const originalPaddingBottom = parseFloat(computed.paddingBottom) || 0;
        this.paddingMap.set(content, { pt: originalPaddingTop, pb: originalPaddingBottom });

        // Ensure initial state: closed panels have 0 height and no padding; open panels use natural paddings
        if (details.open) {
            content.style.height = 'auto';
            content.style.overflow = 'hidden';
            content.style.paddingTop = `${originalPaddingTop}px`;
            content.style.paddingBottom = `${originalPaddingBottom}px`;
        } else {
            content.style.height = '0px';
            content.style.overflow = 'hidden';
            content.style.paddingTop = '0px';
            content.style.paddingBottom = '0px';
        }

        summary.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (typeof event.stopImmediatePropagation === 'function') {
                event.stopImmediatePropagation();
            }
            // Kill any ongoing tweens on this content for reliability
            gsap.killTweensOf(content);

            const isOpen = details.hasAttribute('open');

            if (isOpen) {
                this.animateClose(details, content);
            } else {
                // Close other open items in the same container (manual grouping)
                const container = details.closest('.accordion-container');
                if (container) {
                    const siblings = Array.from(container.querySelectorAll('.accordion-item[open]')).filter((el) => el !== details);
                    siblings.forEach((openDetails) => {
                        const openContent = openDetails.querySelector(this.CONTENT_SELECTOR);
                        if (openContent) {
                            gsap.killTweensOf(openContent);
                            this.animateClose(openDetails, openContent);
                        }
                    });
                }
                this.animateOpen(details, content);
            }
        });

        // Keyboard accessibility: treat Enter/Space like click
        summary.addEventListener('keydown', (event) => {
            const key = event.key;
            if (key === 'Enter' || key === ' ') {
                event.preventDefault();
                summary.click();
            }
        });
    }

    animateOpen(details, content) {
        // Symmetric animation: from current height/padding to target height/padding
        const { pt, pb } = this.paddingMap.get(content) || { pt: 0, pb: 0 };
        const startHeight = content.getBoundingClientRect().height;
        content.style.willChange = 'height, padding';
        content.style.height = `${startHeight}px`;
        // Ensure starting paddings are zero when closed to avoid initial jump
        content.style.paddingTop = content.style.paddingTop || '0px';
        content.style.paddingBottom = content.style.paddingBottom || '0px';

        details.setAttribute('open', '');

        // Measure content height without paddings (paddings are currently 0)
        const contentHeightNoPadding = content.scrollHeight;

        gsap.to(content, {
            height: contentHeightNoPadding,
            paddingTop: pt,
            paddingBottom: pb,
            duration: this.ANIMATION.DURATION,
            ease: this.ANIMATION.EASE,
            onComplete: () => {
                content.style.willChange = '';
                content.style.height = 'auto';
                content.style.paddingTop = `${pt}px`;
                content.style.paddingBottom = `${pb}px`;
            }
        });
    }

    animateClose(details, content) {
        // Symmetric animation: from current height/padding to zero
        const { pt, pb } = this.paddingMap.get(content) || { pt: 0, pb: 0 };
        const currentHeight = content.getBoundingClientRect().height;
        if (currentHeight <= 0.5) {
            // Already collapsed; ensure attribute state is correct
            details.removeAttribute('open');
            return;
        }
        content.style.willChange = 'height, padding';
        content.style.height = `${currentHeight}px`;
        content.style.paddingTop = `${pt}px`;
        content.style.paddingBottom = `${pb}px`;

        gsap.to(content, {
            height: 0,
            paddingTop: 0,
            paddingBottom: 0,
            duration: this.ANIMATION.DURATION,
            ease: this.ANIMATION.EASE,
            onComplete: () => {
                content.style.willChange = '';
                details.removeAttribute('open');
                content.style.height = '0px';
                content.style.paddingTop = '0px';
                content.style.paddingBottom = '0px';
            }
        });
    }

    animateWordPressOpen(item, panel) {
        const { pt, pb } = this.paddingMap.get(panel) || { pt: 0, pb: 0 };
        
        // WordPress has already updated the state, so panel might already be visible
        // Reset to 0 height first for smooth animation
        const currentHeight = panel.getBoundingClientRect().height;
        panel.style.willChange = 'height, padding';
        panel.style.height = '0px';
        panel.style.overflow = 'hidden';
        panel.style.paddingTop = '0px';
        panel.style.paddingBottom = '0px';
        panel.style.minHeight = '0px';
        panel.style.maxHeight = 'none';

        // Force a reflow
        panel.offsetHeight;

        // Measure content height (WordPress has already made it visible)
        const contentHeightNoPadding = panel.scrollHeight;

        gsap.to(panel, {
            height: contentHeightNoPadding,
            paddingTop: pt,
            paddingBottom: pb,
            duration: this.ANIMATION.DURATION,
            ease: this.ANIMATION.EASE,
            onComplete: () => {
                panel.style.willChange = '';
                panel.style.height = 'auto';
                panel.style.paddingTop = `${pt}px`;
                panel.style.paddingBottom = `${pb}px`;
                panel.style.minHeight = 'auto';
                panel.style.maxHeight = 'none';
            }
        });
    }

    animateWordPressClose(item, panel) {
        const { pt, pb } = this.paddingMap.get(panel) || { pt: 0, pb: 0 };
        const currentHeight = panel.getBoundingClientRect().height;
        if (currentHeight <= 0.5) {
            // Already collapsed - WordPress has already updated state
            return;
        }
        panel.style.willChange = 'height, padding';
        panel.style.height = `${currentHeight}px`;
        panel.style.paddingTop = `${pt}px`;
        panel.style.paddingBottom = `${pb}px`;
        panel.style.overflow = 'hidden';

        gsap.to(panel, {
            height: 0,
            paddingTop: 0,
            paddingBottom: 0,
            duration: this.ANIMATION.DURATION,
            ease: this.ANIMATION.EASE,
            onComplete: () => {
                panel.style.willChange = '';
                // WordPress has already updated the state, just ensure styles are correct
                panel.style.height = '0px';
                panel.style.paddingTop = '0px';
                panel.style.paddingBottom = '0px';
                panel.style.minHeight = '0px';
                panel.style.maxHeight = '0px';
            }
        });
    }
}


